import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { Play } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const inputCls = "bg-[#050505] border border-white/15 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50";
const btnCls = "bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-medium px-4 py-2 text-sm transition-colors";

function UserCard({ userId, onClose, onChanged }) {
  const { lang } = useLang();
  const it = lang === "it";
  const [detail, setDetail] = useState(null);
  const [email, setEmail] = useState("");
  const [alphaExp, setAlphaExp] = useState("");
  const [alphaPlan, setAlphaPlan] = useState("");
  const [guardianExp, setGuardianExp] = useState("");

  const load = useCallback(() => api.get(`/admin/users/${userId}`).then((r) => {
    setDetail(r.data);
    const u = r.data.user;
    const future = (d) => (d && new Date(d) > new Date() ? d.slice(0, 10) : "");
    setEmail(u.email || "");
    setAlphaExp(future(u.subscription_expires_at));
    setAlphaPlan(u.subscription_plan || "");
    setGuardianExp(future(u.guardian_expires_at));
  }), [userId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (payload, msg = "OK") => {
    try {
      await api.patch(`/admin/users/${userId}`, payload);
      toast.success(msg); load(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toIso = (d) => new Date(`${d}T23:59:59Z`).toISOString();
  const epoch = new Date(0).toISOString();
  const now = new Date();
  if (!detail) return null;
  const u = detail.user;
  const active = (d) => d && new Date(d) > now;
  const profile = u.profile || {};
  const profileRows = [
    [it ? "Nome" : "Name", u.name],
    [it ? "Attività / Professione" : "Occupation", profile.profession],
    [it ? "Città" : "City", profile.city],
    [it ? "Paese" : "Country", profile.country],
    [it ? "Fascia di reddito" : "Income range", profile.income_range],
    [it ? "Telefono" : "Phone", profile.phone],
  ];
  const secCls = "border border-white/10 bg-[#0d0d0d] p-4 space-y-3";
  const smallBtn = "border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-2 py-1 text-xs transition-colors";
  const skyBtn = "border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 px-2 py-1 text-xs transition-colors";
  const redBtn = "border border-red-500/40 text-red-400 hover:bg-red-500/10 px-2 py-1 text-xs transition-colors";

  return (
    <div data-testid="admin-user-card" className="border border-emerald-500/30 bg-[#0a0a0a] p-4 mb-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-medium text-white">{it ? "Scheda utente" : "User card"} — <span className="font-mono text-emerald-400">{u.email}</span></h3>
        <button data-testid="admin-user-card-close" onClick={onClose} className="border border-white/15 text-slate-300 hover:bg-white/5 px-3 py-1 text-xs">✕ {it ? "Chiudi" : "Close"}</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className={secCls}>
          <p className="text-xs uppercase tracking-widest text-slate-500">{it ? "Account" : "Account"}</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="admin-user-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} flex-1 min-w-[220px]`} />
            <button data-testid="admin-user-email-save" onClick={() => patch({ email }, it ? "Email aggiornata" : "Email updated")} className={btnCls}>{it ? "Salva email" : "Save email"}</button>
          </div>
          <p className="text-xs text-slate-500 font-mono">
            {it ? "Ruolo" : "Role"}: <span className="text-slate-300">{u.role}</span> · {it ? "Registrato" : "Registered"}: <span className="text-slate-300">{(u.created_at || "").slice(0, 10) || "—"}</span> · Auth: <span className="text-slate-300">{u.auth_provider}</span>
          </p>
          <p className="text-xs text-slate-500 font-mono">
            Trial: <span data-testid="admin-user-trial-status" className={active(u.trial_expires_at) ? "text-amber-400" : "text-slate-300"}>
              {active(u.trial_expires_at) ? `${it ? "attivo fino al" : "active until"} ${u.trial_expires_at.slice(0, 10)}` : u.trial_used ? (it ? "usato" : "used") : (it ? "mai usato" : "never used")}
            </span>
          </p>
          <div className="flex gap-2">
            <button data-testid="admin-user-trial-reset" onClick={() => patch({ trial_used: false, trial_expires_at: epoch }, it ? "Trial riabilitato" : "Trial reset")} className={smallBtn}>{it ? "Riabilita trial" : "Reset trial"}</button>
            {active(u.trial_expires_at) && (
              <button data-testid="admin-user-trial-revoke" onClick={() => patch({ trial_expires_at: epoch }, it ? "Trial revocato" : "Trial revoked")} className={redBtn}>{it ? "Revoca trial" : "Revoke trial"}</button>
            )}
          </div>
        </div>

        <div className={secCls}>
          <p className="text-xs uppercase tracking-widest text-slate-500">{it ? "Profilo facoltativo" : "Optional profile"}</p>
          <div data-testid="admin-user-profile" className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs font-mono">
            {profileRows.map(([k, v]) => (
              <div key={k}><span className="text-slate-500">{k}:</span> <span className="text-slate-200">{v || "—"}</span></div>
            ))}
          </div>
        </div>

        <div className={`${secCls} border-emerald-500/20`}>
          <p className="text-xs uppercase tracking-widest text-emerald-400">Bund Alpha — {it ? "abbonamento" : "subscription"}</p>
          <p className="text-xs font-mono">
            <span className={active(u.subscription_expires_at) ? "text-emerald-400" : "text-slate-500"}>
              {active(u.subscription_expires_at)
                ? `${u.subscription_plan || "—"} · ${it ? "scade il" : "expires"} ${u.subscription_expires_at.slice(0, 10)}`
                : (it ? "Nessun abbonamento attivo" : "No active subscription")}
            </span>
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <select data-testid="admin-user-alpha-plan" value={alphaPlan} onChange={(e) => setAlphaPlan(e.target.value)} className={inputCls}>
              <option value="">{it ? "Piano…" : "Plan…"}</option>
              {["Basic", "Professional", "Business", "Manual"].map((p) => <option key={p}>{p}</option>)}
            </select>
            <input data-testid="admin-user-alpha-expiry" type="date" value={alphaExp} onChange={(e) => setAlphaExp(e.target.value)} className={inputCls} />
            <button data-testid="admin-user-alpha-save" disabled={!alphaExp}
              onClick={() => patch({ subscription_expires_at: toIso(alphaExp), ...(alphaPlan ? { subscription_plan: alphaPlan } : {}) }, "OK")} className={btnCls}>{it ? "Salva" : "Save"}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {[3, 6, 12].map((m) => (
              <button key={m} data-testid={`admin-user-alpha-grant-${m}`} onClick={() => patch({ grant_months: m })} className={smallBtn}>+{m} {it ? "mesi" : "months"}</button>
            ))}
            <button data-testid="admin-user-alpha-revoke" onClick={() => patch({ subscription_expires_at: epoch }, it ? "Alpha revocato" : "Alpha revoked")} className={redBtn}>{it ? "Revoca" : "Revoke"}</button>
          </div>
        </div>

        <div className={`${secCls} border-sky-500/20`}>
          <p className="text-xs uppercase tracking-widest text-sky-400">Bund Guardian — {it ? "abbonamento" : "subscription"}</p>
          <p className="text-xs font-mono">
            <span className={active(u.guardian_expires_at) ? "text-sky-400" : "text-slate-500"}>
              {active(u.guardian_expires_at)
                ? `${u.guardian_plan || "Guardian"} · ${it ? "scade il" : "expires"} ${u.guardian_expires_at.slice(0, 10)}`
                : (it ? "Nessun abbonamento attivo" : "No active subscription")}
            </span>
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="admin-user-guardian-expiry" type="date" value={guardianExp} onChange={(e) => setGuardianExp(e.target.value)} className={inputCls} />
            <button data-testid="admin-user-guardian-save" disabled={!guardianExp}
              onClick={() => patch({ guardian_expires_at: toIso(guardianExp), guardian_plan: u.guardian_plan || "Manual" })}
              className="bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-black font-medium px-4 py-2 text-sm transition-colors">{it ? "Salva" : "Save"}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="admin-user-guardian-grant-12" onClick={() => patch({ grant_guardian_months: 12 })} className={skyBtn}>+12 {it ? "mesi" : "months"}</button>
            <button data-testid="admin-user-guardian-revoke" onClick={() => patch({ guardian_expires_at: epoch }, it ? "Guardian revocato" : "Guardian revoked")} className={redBtn}>{it ? "Revoca" : "Revoke"}</button>
          </div>
        </div>
      </div>

      <div className={secCls}>
        <p className="text-xs uppercase tracking-widest text-slate-500">{it ? "Storico pagamenti" : "Payment history"}</p>
        {detail.payments.length === 0 ? (
          <p data-testid="admin-user-no-payments" className="text-xs text-slate-500">{it ? "Nessun pagamento registrato." : "No payments recorded."}</p>
        ) : (
          <table data-testid="admin-user-payments" className="w-full text-xs font-mono">
            <thead><tr className="text-slate-500 uppercase text-[10px] tracking-wider">
              <th className="text-left px-2 py-1">{it ? "Data" : "Date"}</th>
              <th className="text-left px-2 py-1">{it ? "Metodo" : "Method"}</th>
              <th className="text-left px-2 py-1">{it ? "Piano" : "Plan"}</th>
              <th className="text-right px-2 py-1">{it ? "Importo" : "Amount"}</th>
              <th className="text-right px-2 py-1">{it ? "Stato" : "Status"}</th></tr></thead>
            <tbody>
              {detail.payments.map((p, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-2 py-1.5 text-slate-400">{(p.date || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-2 py-1.5">{p.method === "crypto" ? <span className="text-amber-400">Crypto {p.currency}</span> : <span className="text-slate-200">{it ? "Carta / Stripe" : "Card / Stripe"}</span>}</td>
                  <td className={`px-2 py-1.5 ${p.product === "guardian" ? "text-sky-400" : "text-emerald-400"}`}>{p.plan}</td>
                  <td className="px-2 py-1.5 text-right text-slate-200">{p.amount_eur != null ? `€${p.amount_eur}` : "—"}</td>
                  <td className={`px-2 py-1.5 text-right ${["paid", "approved"].includes(p.status) ? "text-emerald-400" : p.status === "rejected" ? "text-red-400" : "text-amber-400"}`}>{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const { lang } = useLang();
  const it = lang === "it";
  const [users, setUsers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const now = new Date();
  const active = (d) => d && new Date(d) > now;

  return (
    <div>
      {openId && <div className="p-2"><UserCard userId={openId} onClose={() => setOpenId(null)} onChanged={load} /></div>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead><tr className="text-slate-500 uppercase text-[10px] tracking-wider">
            <th className="text-left px-3 py-2">Email</th><th className="text-left px-2 py-2">{it ? "Ruolo" : "Role"}</th>
            <th className="text-left px-2 py-2">Bund Alpha</th>
            <th className="text-left px-2 py-2">Guardian</th>
            <th className="text-left px-2 py-2">Trial</th>
            <th className="text-right px-3 py-2">{it ? "Azioni" : "Actions"}</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-white/5">
                <td className="px-3 py-2 text-slate-200">{u.email}</td>
                <td className="px-2 py-2 text-slate-400">{u.role}</td>
                <td className="px-2 py-2">
                  {active(u.subscription_expires_at)
                    ? <span className="text-emerald-400">{u.subscription_plan || "attivo"} → {u.subscription_expires_at.slice(0, 10)}</span>
                    : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-2 py-2">
                  {active(u.guardian_expires_at)
                    ? <span className="text-sky-400">{u.guardian_plan || "Guardian"} → {u.guardian_expires_at.slice(0, 10)}</span>
                    : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-2 py-2">
                  {active(u.trial_expires_at)
                    ? <span className="text-amber-400">→ {u.trial_expires_at.slice(0, 10)}</span>
                    : <span className="text-slate-600">{u.trial_used ? (it ? "usato" : "used") : "—"}</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`admin-user-open-${u.id}`} onClick={() => setOpenId(openId === u.id ? null : u.id)}
                    className="border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-3 py-1 transition-colors">
                    {it ? "Scheda" : "Card"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignalsTab() {
  const { t } = useLang();
  const [signals, setSignals] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/signals").then((r) => setSignals(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/admin/signals/${editing.id}`, {
        direction: editing.direction, motivation_it: editing.motivation_it,
        motivation_en: editing.motivation_en, note: editing.note });
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2">
      {editing && (
        <div data-testid="admin-signal-editor" className="border border-emerald-500/30 p-4 space-y-3 bg-[#0d0d0d]">
          <p className="font-mono text-sm text-slate-300">{editing.date}</p>
          <select data-testid="admin-signal-direction" value={editing.direction}
            onChange={(e) => setEditing({ ...editing, direction: e.target.value })} className={inputCls}>
            {["LONG", "SHORT", "HOLD"].map((d) => <option key={d}>{d}</option>)}
          </select>
          <textarea data-testid="admin-signal-motivation-it" value={editing.motivation_it} rows={2}
            onChange={(e) => setEditing({ ...editing, motivation_it: e.target.value })} className={`${inputCls} w-full`} />
          <textarea value={editing.motivation_en} rows={2}
            onChange={(e) => setEditing({ ...editing, motivation_en: e.target.value })} className={`${inputCls} w-full`} />
          <input data-testid="admin-signal-note" placeholder="Nota qualitativa (opzionale)" value={editing.note || ""}
            onChange={(e) => setEditing({ ...editing, note: e.target.value })} className={`${inputCls} w-full`} />
          <div className="flex gap-2">
            <button data-testid="admin-signal-save" onClick={save} className={btnCls}>{t("save")}</button>
            <button onClick={() => setEditing(null)} className="border border-white/15 text-slate-300 px-4 py-2 text-sm hover:bg-white/5">{t("cancel")}</button>
          </div>
        </div>
      )}
      <table className="w-full text-xs font-mono">
        <tbody>
          {signals.map((s) => (
            <tr key={s.id} data-testid={`admin-signal-row-${s.id}`} className="border-t border-white/5">
              <td className="px-3 py-2 text-slate-400">{s.date}</td>
              <td className={`px-2 py-2 font-bold ${s.direction === "LONG" ? "text-emerald-400" : s.direction === "SHORT" ? "text-red-400" : "text-amber-400"}`}>{s.direction}</td>
              <td className="px-2 py-2 text-slate-500 hidden md:table-cell truncate max-w-md">{s.motivation_it}</td>
              <td className="px-2 py-2 text-amber-400">{s.edited ? "✎" : ""}</td>
              <td className="px-3 py-2 text-right">
                <button data-testid={`admin-signal-edit-${s.id}`} onClick={() => setEditing(s)}
                  className="border border-white/15 text-slate-300 hover:bg-white/5 px-3 py-1 transition-colors">{t("edit")}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportsTab() {
  const { t } = useLang();
  const [reports, setReports] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/public/reports", { params: { limit: 60 } }).then((r) => setReports(r.data.reports)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/admin/reports/${editing.id}`, {
        title_it: editing.title_it, title_en: editing.title_en,
        content_it: editing.content_it, content_en: editing.content_en });
      toast.success("OK"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2">
      {editing && (
        <div data-testid="admin-report-editor" className="border border-emerald-500/30 p-4 space-y-3 bg-[#0d0d0d]">
          <input data-testid="admin-report-title-it" value={editing.title_it}
            onChange={(e) => setEditing({ ...editing, title_it: e.target.value })} className={`${inputCls} w-full`} />
          <textarea data-testid="admin-report-content-it" value={editing.content_it} rows={8}
            onChange={(e) => setEditing({ ...editing, content_it: e.target.value })} className={`${inputCls} w-full`} />
          <textarea value={editing.content_en} rows={8}
            onChange={(e) => setEditing({ ...editing, content_en: e.target.value })} className={`${inputCls} w-full`} />
          <div className="flex gap-2">
            <button data-testid="admin-report-save" onClick={save} className={btnCls}>{t("save")}</button>
            <button onClick={() => setEditing(null)} className="border border-white/15 text-slate-300 px-4 py-2 text-sm hover:bg-white/5">{t("cancel")}</button>
          </div>
        </div>
      )}
      {reports.map((r) => (
        <div key={r.id} className="flex items-center gap-3 border-t border-white/5 px-3 py-2 text-xs font-mono">
          <span className="text-slate-400">{r.date}</span>
          <span className={r.type === "weekly" ? "text-amber-400" : "text-emerald-400"}>{r.type}</span>
          <span className="flex-1 text-slate-300 truncate">{r.title_it}</span>
          {r.edited && <span className="text-amber-400">✎</span>}
          <button data-testid={`admin-report-edit-${r.id}`} onClick={() => setEditing(r)}
            className="border border-white/15 text-slate-300 hover:bg-white/5 px-3 py-1 transition-colors">{t("edit")}</button>
        </div>
      ))}
    </div>
  );
}

function GuardianRebuildBox() {
  const { lang } = useLang();
  const it = lang === "it";
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job || job.status !== "running") return;
    const t = setInterval(() => {
      api.get(`/admin/import/status/${job.id}`).then((r) => setJob(r.data)).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [job]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/guardian/rebuild");
      setJob({ id: data.job_id, status: "running", done: 0, total: 0 });
      toast.success(it ? "Ricostruzione Guardian avviata" : "Guardian rebuild started");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div data-testid="guardian-rebuild-box" className="border border-sky-500/25 p-4 space-y-2 bg-[#0B1220]">
      <p className="text-sm text-slate-200">{it ? "Storico sistema Guardian" : "Guardian system history"}</p>
      <p className="text-xs text-slate-500 leading-relaxed">
        {it
          ? "Ricostruisce da zero segnali e operazioni del sistema Guardian rielaborando tutti i prezzi EOD presenti (2008-oggi). Da eseguire UNA VOLTA dopo il primo deploy in produzione o dopo un import CSV fatto prima dell'introduzione di Guardian. Richiede 1-2 minuti."
          : "Rebuilds Guardian signals and operations from scratch by replaying all stored EOD prices (2008-today). Run it ONCE after the first production deploy or after a CSV import done before Guardian was introduced. Takes 1-2 minutes."}
      </p>
      <div className="flex items-center gap-3">
        <button data-testid="guardian-rebuild-btn" onClick={start} disabled={busy || job?.status === "running"}
          className="border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50 px-4 py-2 text-sm transition-colors">
          {it ? "Ricostruisci storico Guardian" : "Rebuild Guardian history"}
        </button>
        {job && (
          <span data-testid="guardian-rebuild-status" className={`text-xs font-mono ${job.status === "done" ? "text-sky-400" : job.status === "failed" ? "text-red-400" : "text-amber-400"}`}>
            {job.status === "running" && `${it ? "in corso" : "running"}… ${job.done}/${job.total || "?"}`}
            {job.status === "done" && `${it ? "completato" : "done"} — ${job.g_trades ?? ""} trades`}
            {job.status === "failed" && `${it ? "errore" : "failed"}: ${job.error}`}
          </span>
        )}
      </div>
    </div>
  );
}

function EodTab() {
  const { t } = useLang();
  const [data, setData] = useState({ prices: [], pending: [] });
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), open: "", close: "" });
  const load = useCallback(() => api.get("/admin/eod").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/admin/eod", { date: form.date, open: parseFloat(form.open), close: parseFloat(form.close) });
      toast.success("OK"); setForm({ ...form, open: "", close: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runEngine = async () => {
    try {
      const { data: res } = await api.post("/admin/run-engine");
      const msg = res.status === "processed"
        ? `${res.date}: ${t("signal_title")} ${res.signal || "—"}`
        : res.status === "awaiting_manual" ? `${res.date}: ${t("pending_prices")}` : res.status;
      toast.success(msg); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="border border-white/10 p-4 space-y-3 bg-[#0d0d0d]">
        <p className="text-sm text-slate-200">{t("eod_manual")}</p>
        <div className="flex flex-wrap gap-3">
          <input data-testid="admin-eod-date" type="date" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
          <input data-testid="admin-eod-open" type="number" step="0.01" placeholder={t("price_open")} value={form.open}
            onChange={(e) => setForm({ ...form, open: e.target.value })} className={inputCls} />
          <input data-testid="admin-eod-close" type="number" step="0.01" placeholder={t("price_close")} value={form.close}
            onChange={(e) => setForm({ ...form, close: e.target.value })} className={inputCls} />
          <button data-testid="admin-eod-submit" onClick={submit} disabled={!form.open || !form.close} className={btnCls}>{t("save")}</button>
          <button data-testid="admin-run-engine" onClick={runEngine}
            className="border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Play className="w-3.5 h-3.5" /> {t("run_engine")}
          </button>
        </div>
        <p className="text-xs text-slate-500">{t("pending_prices")}: {data.pending.length ? data.pending.map((p) => p.date).join(", ") : t("none")}</p>
      </div>
      <GuardianRebuildBox />
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-slate-500 uppercase text-[10px] tracking-wider">
          <th className="text-left px-3 py-2">{t("date")}</th><th className="text-left px-2 py-2">{t("col_contract")}</th>
          <th className="text-right px-2 py-2">{t("price_open")}</th><th className="text-right px-2 py-2">{t("price_close")}</th>
          <th className="text-right px-3 py-2">{t("source")}</th></tr></thead>
        <tbody>
          {data.prices.map((p) => (
            <tr key={p.id} className="border-t border-white/5">
              <td className="px-3 py-2 text-slate-300">{p.date}</td>
              <td className="px-2 py-2 text-slate-400">{p.contract}</td>
              <td className="px-2 py-2 text-right text-slate-300">{p.open}</td>
              <td className="px-2 py-2 text-right text-slate-300">{p.close}</td>
              <td className={`px-3 py-2 text-right ${p.source === "manual" ? "text-amber-400" : "text-slate-500"}`}>{p.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StripeTab() {
  const { lang } = useLang();
  const it = lang === "it";
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/stripe/config").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stripe/config", { ...form, ...extra });
      setCfg(data); setForm({ secret_key: "", publishable_key: "", webhook_secret: "" });
      toast.success(it ? "Configurazione Stripe salvata" : "Stripe configuration saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <p className="text-xs text-slate-500 px-3 py-2">…</p>;
  const inputCls = "w-full bg-[#111111] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-slate-600 font-mono";
  return (
    <div className="p-4 space-y-6 max-w-2xl">
      <div className="border border-white/10 bg-white/[0.02] p-4 text-xs space-y-1.5 font-mono">
        <p className="text-slate-400">{it ? "Chiave attiva" : "Active key"}: <span className="text-white">{cfg.secret_key_masked}</span>
          <span className={`ml-2 px-1.5 py-0.5 border ${cfg.mode === "live" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>{cfg.mode.toUpperCase()}</span>
          <span className="ml-2 text-slate-600">({cfg.source === "admin" ? (it ? "impostata da admin" : "set by admin") : "env"})</span></p>
        {cfg.account && <p className="text-slate-500">Account: {cfg.account.id} · {it ? "paese" : "country"}: {cfg.account.country}</p>}
        <p className="text-slate-500">Webhook: {cfg.webhook_secret_masked}</p>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-white">{it ? "Sostituisci chiavi (lascia vuoto per non cambiare)" : "Replace keys (leave empty to keep)"}</h3>
        <input data-testid="stripe-secret-input" type="password" placeholder="sk_live_... / sk_test_..." value={form.secret_key}
          onChange={(e) => setForm({ ...form, secret_key: e.target.value })} className={inputCls} />
        <input data-testid="stripe-publishable-input" placeholder="pk_live_... / pk_test_..." value={form.publishable_key}
          onChange={(e) => setForm({ ...form, publishable_key: e.target.value })} className={inputCls} />
        <input data-testid="stripe-webhook-input" type="password" placeholder="whsec_... (webhook secret)" value={form.webhook_secret}
          onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} className={inputCls} />
        <p className="text-[11px] text-slate-600 leading-relaxed">{it
          ? "La chiave segreta viene VALIDATA su Stripe prima del salvataggio. Le chiavi salvate qui hanno priorità su quelle d'ambiente e sopravvivono ai deploy. Dopo il passaggio a un nuovo account live, esegui anche la ricreazione dei prodotti (setup prezzi)."
          : "The secret key is VALIDATED against Stripe before saving. Keys saved here take priority over environment ones and survive deploys. After switching to a new live account, also recreate the products (price setup)."}</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="stripe-save-btn" disabled={busy || (!form.secret_key && !form.publishable_key && !form.webhook_secret)}
            onClick={() => save()} className={btnCls}>{it ? "Salva chiavi" : "Save keys"}</button>
          <button data-testid="stripe-reset-btn" disabled={busy || cfg.source !== "admin"} onClick={() => save({ reset_to_env: true })}
            className="border border-white/15 text-slate-400 hover:bg-white/5 px-3 py-1.5 text-xs uppercase tracking-widest disabled:opacity-40">
            {it ? "Torna alle chiavi d'ambiente" : "Revert to env keys"}</button>
        </div>
      </div>
      <div className="border-t border-white/10 pt-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input data-testid="stripe-tax-toggle" type="checkbox" checked={cfg.automatic_tax}
            onChange={(e) => save({ automatic_tax: e.target.checked })} className="accent-emerald-500 w-4 h-4" />
          <div>
            <p className="text-sm text-white">{it ? "Calcolo IVA automatico (Stripe Tax)" : "Automatic VAT calculation (Stripe Tax)"}</p>
            <p className="text-[11px] text-slate-500">{it
              ? "Disattivato: il cliente paga il prezzo esatto senza imposte aggiunte (regime extra-UE — verifica la conformità col tuo commercialista)."
              : "Off: the customer pays the exact price with no added taxes (extra-EU regime — verify compliance with your tax advisor)."}</p>
          </div>
        </label>
      </div>
    </div>
  );
}

const WALLET_META = [
  { currency: "BTC", label: "Bitcoin", hint: "bc1… (bech32) o 1…/3… (legacy)" },
  { currency: "USDC", label: "USDC (Ethereum ERC-20)", hint: "0x… (40 hex) — indirizzo Ethereum ERC-20" },
  { currency: "USDT", label: "USDT (Tron TRC-20)", hint: "T… (34 caratteri) — indirizzo Tron TRC-20" },
  { currency: "EURC", label: "EURC (Ethereum ERC-20)", hint: "0x… (40 hex) — indirizzo Ethereum ERC-20" },
];

function WalletCard({ meta, wallet, onSaved, it }) {
  const [w, setW] = useState({ network: "", address: "", memo: "", enabled: true, ...wallet });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full bg-[#111111] border border-white/15 px-3 py-2 text-xs text-white placeholder:text-slate-600 font-mono";
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/crypto/wallets", { currency: meta.currency, ...w });
      toast.success(`${meta.currency}: ${it ? "salvato" : "saved"}`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`wallet-card-${meta.currency}`} className="border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-mono font-bold text-amber-400">{meta.currency}</span>
          <span className="text-xs text-slate-500 ml-2">{meta.label}</span>
          <p className="text-[10px] text-slate-600">{meta.hint}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input data-testid={`wallet-enabled-${meta.currency}`} type="checkbox" checked={w.enabled}
            onChange={(e) => setW({ ...w, enabled: e.target.checked })} className="accent-emerald-500" />
          {it ? "Abilitato" : "Enabled"}
        </label>
      </div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-4 mt-3">
        <div className="space-y-2">
          <input data-testid={`wallet-address-${meta.currency}`} placeholder={it ? "indirizzo" : "address"} value={w.address}
            onChange={(e) => setW({ ...w, address: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`wallet-network-${meta.currency}`} placeholder={it ? "rete" : "network"} value={w.network}
              onChange={(e) => setW({ ...w, network: e.target.value })} className={inputCls} />
            <input data-testid={`wallet-memo-${meta.currency}`} placeholder={it ? "memo/avviso (opz.)" : "memo/notice (opt.)"} value={w.memo}
              onChange={(e) => setW({ ...w, memo: e.target.value })} className={inputCls} />
          </div>
          <div className="flex items-center gap-3">
            <button data-testid={`wallet-save-${meta.currency}`} disabled={busy} onClick={save} className={btnCls}>
              {it ? "Salva modifiche" : "Save changes"}</button>
            {wallet?.updated_at && <span className="text-[10px] text-slate-600">{it ? "ultimo agg." : "updated"}: {wallet.updated_at.slice(0, 16).replace("T", " ")}</span>}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          {w.address ? (
            <div className="bg-white p-2"><QRCodeCanvas value={w.address} size={96} /></div>
          ) : (
            <div className="w-[112px] h-[112px] border border-dashed border-white/15 flex items-center justify-center text-[10px] text-slate-600">QR</div>
          )}
          <span className="text-[10px] text-slate-600">{it ? "anteprima cliente" : "customer preview"}</span>
        </div>
      </div>
    </div>
  );
}

function CryptoTab() {
  const { t, lang } = useLang();
  const it = lang === "it";
  const [reqs, setReqs] = useState([]);
  const [wallets, setWallets] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/crypto-requests").then((r) => setReqs(r.data));
    api.get("/admin/crypto/wallets").then((r) => setWallets(r.data.wallets));
  }, []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    try { await api.patch(`/admin/crypto-requests/${id}`, { status }); toast.success("OK"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-white mb-1">{it ? "Wallet crypto per i pagamenti" : "Crypto payment wallets"}</h3>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed">{it
          ? "Inserisci gli indirizzi dei tuoi wallet self-custodial: ogni indirizzo viene validato per il formato della sua rete prima del salvataggio. I clienti li vedono (con QR code) nella finestra di pagamento crypto. Disabilita le valute che non vuoi accettare."
          : "Enter your self-custodial wallet addresses: each address is validated against its network format before saving. Customers see them (with QR code) in the crypto payment dialog. Disable currencies you don't accept."}</p>
        <div className="grid lg:grid-cols-2 gap-4">
          {wallets && WALLET_META.map((m) => (
            <WalletCard key={m.currency} meta={m} it={it}
              wallet={wallets.find((x) => x.currency === m.currency)} onSaved={load} />
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "Richieste di pagamento crypto" : "Crypto payment requests"} ({reqs.length})</h3>
        {!reqs.length && <p className="text-xs text-slate-600">{it ? "Nessuna richiesta ricevuta finora." : "No requests received yet."}</p>}
        <table className="w-full text-xs font-mono">
      <tbody>
        {reqs.map((r) => (
          <tr key={r.id} data-testid={`admin-crypto-row-${r.id}`} className="border-t border-white/5">
            <td className="px-3 py-2 text-slate-300">{r.user_email}</td>
            <td className="px-2 py-2 text-slate-400">{r.lookup_key}</td>
            <td className="px-2 py-2 text-amber-400">{r.currency}</td>
            <td className="px-2 py-2 text-slate-500 truncate max-w-[160px]">{r.tx_hash}</td>
            <td className={`px-2 py-2 ${r.status === "approved" ? "text-emerald-400" : r.status === "rejected" ? "text-red-400" : "text-amber-400"}`}>{r.status}</td>
            <td className="px-3 py-2 text-right space-x-1">
              {r.status === "pending" && (<>
                <button data-testid={`admin-crypto-approve-${r.id}`} onClick={() => review(r.id, "approved")}
                  className="border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-3 py-1 transition-colors">{t("approve")}</button>
                <button data-testid={`admin-crypto-reject-${r.id}`} onClick={() => review(r.id, "rejected")}
                  className="border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-1 transition-colors">{t("reject")}</button>
              </>)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
      </div>
    </div>
  );
}

function ImportTab() {
  const { t } = useLang();
  const [priceFile, setPriceFile] = useState(null);
  const [replace, setReplace] = useState(true);
  const [rebuild, setRebuild] = useState(true);
  const [job, setJob] = useState(null);
  const [tradeFile, setTradeFile] = useState(null);
  const [replaceTrades, setReplaceTrades] = useState(true);
  const [tradeResult, setTradeResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job || job.status !== "running") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/import/status/${job.id || job.job_id}`);
        setJob(data);
        if (data.status !== "running") clearInterval(iv);
      } catch { clearInterval(iv); }
    }, 2000);
    return () => clearInterval(iv);
  }, [job?.status, job?.id, job?.job_id]);

  const uploadPrices = async () => {
    if (!priceFile) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", priceFile);
    fd.append("replace", replace);
    fd.append("rebuild", rebuild);
    try {
      const { data } = await api.post("/admin/import/prices", fd);
      setJob({ ...data, status: "running" });
      toast.success(`${data.rows} rows — ${data.skipped} ${t("import_skipped")}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadTrades = async () => {
    if (!tradeFile) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", tradeFile);
    fd.append("replace", replaceTrades);
    try {
      const { data } = await api.post("/admin/import/trades", fd);
      setTradeResult(data);
      toast.success(`${t("import_done")}: ${data.imported} trades`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 p-4">
      <div data-testid="import-prices-section" className="border border-white/10 p-4 space-y-3 bg-[#0d0d0d]">
        <p className="text-sm text-slate-200 font-medium">{t("import_prices_title")}</p>
        <p className="text-xs text-slate-500">{t("import_prices_help")}</p>
        <input data-testid="import-prices-file" type="file" accept=".csv,.txt" onChange={(e) => setPriceFile(e.target.files[0])}
          className="block text-xs text-slate-400 file:mr-3 file:border file:border-white/15 file:bg-transparent file:text-slate-300 file:px-3 file:py-1.5 file:text-xs" />
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input data-testid="import-prices-replace" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> {t("import_replace")}
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input data-testid="import-prices-rebuild" type="checkbox" checked={rebuild} onChange={(e) => setRebuild(e.target.checked)} /> {t("import_rebuild")}
        </label>
        <button data-testid="import-prices-submit" onClick={uploadPrices} disabled={!priceFile || busy || job?.status === "running"} className={btnCls}>
          {t("import_upload")}
        </button>
        {job && (
          <p data-testid="import-prices-status" className={`text-xs font-mono ${job.status === "failed" ? "text-red-400" : job.status === "done" ? "text-emerald-400" : "text-amber-400"}`}>
            {job.status === "running" && `${t("import_running")} ${job.phase === "rebuild" ? `${job.done || 0}/${job.total || "?"}` : ""}`}
            {job.status === "done" && `${t("import_done")} — ${JSON.stringify(job.result)}`}
            {job.status === "failed" && `${t("import_failed")}: ${job.error}`}
          </p>
        )}
      </div>
      <div data-testid="import-trades-section" className="border border-white/10 p-4 space-y-3 bg-[#0d0d0d]">
        <p className="text-sm text-slate-200 font-medium">{t("import_trades_title")}</p>
        <p className="text-xs text-slate-500">{t("import_trades_help")}</p>
        <input data-testid="import-trades-file" type="file" accept=".csv,.txt" onChange={(e) => setTradeFile(e.target.files[0])}
          className="block text-xs text-slate-400 file:mr-3 file:border file:border-white/15 file:bg-transparent file:text-slate-300 file:px-3 file:py-1.5 file:text-xs" />
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input data-testid="import-trades-replace" type="checkbox" checked={replaceTrades} onChange={(e) => setReplaceTrades(e.target.checked)} /> {t("import_replace")}
        </label>
        <button data-testid="import-trades-submit" onClick={uploadTrades} disabled={!tradeFile || busy} className={btnCls}>{t("import_upload")}</button>
        {tradeResult && (
          <p data-testid="import-trades-status" className="text-xs font-mono text-emerald-400">
            {t("import_done")} — {tradeResult.imported} trades, {tradeResult.skipped} {t("import_skipped")}
          </p>
        )}
      </div>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data)); }, []);
  return (
    <div className="space-y-1">
      {logs.map((l) => (
        <div key={l.id} className="border-t border-white/5 px-3 py-2 text-xs font-mono flex flex-wrap gap-3">
          <span className="text-slate-500">{l.timestamp?.slice(0, 16).replace("T", " ")}</span>
          <span className="text-emerald-400">{l.entity}</span>
          <span className="text-slate-400">{l.entity_id}</span>
          <span className="text-slate-300">{l.admin_email}</span>
          <span className="text-slate-500 truncate flex-1">{JSON.stringify(l.changes)}</span>
        </div>
      ))}
    </div>
  );
}

function EaTab() {
  const { lang } = useLang();
  const it = lang === "it";
  const smallBtn = "border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-2 py-1 text-xs transition-colors";
  const redBtn = "border border-red-500/40 text-red-400 hover:bg-red-500/10 px-2 py-1 text-xs transition-colors";
  const [data, setData] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantProduct, setGrantProduct] = useState("hydra");
  const [directAcc, setDirectAcc] = useState("");
  const [directProduct, setDirectProduct] = useState("hydra");
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/ea/overview").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (product) => {
    const f = files[product];
    if (!f) return;
    setBusy(product);
    const fd = new FormData();
    fd.append("product", product);
    fd.append("file", f);
    try {
      await api.post("/admin/ea/upload", fd);
      toast.success(it ? "File .ex4 caricato" : ".ex4 file uploaded");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const downloadSrc = async (product) => {
    try {
      const res = await api.get(`/admin/ea/source/${product}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = product === "hydra" ? "HYDRA_StatArb.mq4" : "ATLAS_Breakout.mq4"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download sorgente"); }
  };

  const resetLic = async (user_id, product) => {
    await api.post("/admin/ea/license/reset", { user_id, product });
    toast.success(it ? "Licenza resettata" : "License reset");
    load();
  };

  if (!data) return <p className="text-xs text-slate-500 px-3 py-2">…</p>;
  const steps_it = [
    ["1. Scarica i sorgenti", "Usa i bottoni \u201cScarica sorgente\u201d qui sotto: ottieni ATLAS_Breakout.mq4 e HYDRA_StatArb.mq4. Il default LicenseServer punta gia' a https://app.bundstrategy.com: non serve modificare nulla."],
    ["2. Compila in MetaEditor (sul tuo PC con MT4)", "Copia i .mq4 in MQL4/Experts (File \u2192 Apri scheda dati), apri MetaEditor (F4), doppio click sul file nel Navigator e premi Compila (F7). Devi vedere \u201c0 errors, 0 warnings\u201d: nella stessa cartella nasce il .ex4."],
    ["3. Verifica rapida del .ex4", "Trascina il .ex4 su un grafico del TUO MT4: ora deve comparire l'alert \u201cTRADING BLOCCATO sul conto...\u201d (il tuo conto non e' registrato come acquirente \u2014 e' il comportamento corretto). L'EA e' fail-closed: senza verifica di licenza riuscita non opera MAI, anche se l'URL non e' in whitelist. Poi rimuovilo dal grafico."],
    ["4. Carica qui i .ex4", "Usa i campi upload qui sotto (uno per prodotto). Da quel momento gli acquirenti scaricano il file dalla loro pagina Download."],
    ["5. Aggiornamenti futuri", "Se modifico i sorgenti, ripeti 1\u21924: il nuovo upload sostituisce il vecchio e tutti i clienti scaricano l'ultima versione."],
  ];
  const steps_en = [
    ["1. Download the sources", "Use the \u201cDownload source\u201d buttons below to get both .mq4 files. The LicenseServer default already points to https://app.bundstrategy.com: nothing to edit."],
    ["2. Compile in MetaEditor (on your MT4 PC)", "Copy the .mq4 into MQL4/Experts (File \u2192 Open Data Folder), open MetaEditor (F4), double-click the file and press Compile (F7). You must see \u201c0 errors, 0 warnings\u201d: the .ex4 appears in the same folder."],
    ["3. Quick .ex4 check", "Drag the .ex4 onto a chart of YOUR MT4: the \u201cTRADING BLOCKED on account...\u201d alert must now appear (your account isn't registered as a buyer \u2014 correct behaviour). The EA is fail-closed: without a successful license check it NEVER trades, even if the URL isn't whitelisted. Then remove it."],
    ["4. Upload the .ex4 here", "Use the upload fields below (one per product). From that moment buyers download the file from their Download page."],
    ["5. Future updates", "If the sources change, repeat 1\u21924: the new upload replaces the old one and every client downloads the latest version."],
  ];
  const steps = it ? steps_it : steps_en;
  return (
    <div className="p-4 space-y-6">
      <div data-testid="admin-ea-guide" className="border border-emerald-500/20 bg-emerald-500/[0.03] p-4">
        <h3 className="text-sm font-semibold text-white mb-1">
          {it ? "Procedura: compilazione e pubblicazione dei .ex4" : "Procedure: compiling and publishing the .ex4 files"}
        </h3>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed">
          {it
            ? "Il file .ex4 e' UNICO per tutti i clienti: l'univocita' non sta nel file ma nella licenza server-side. A ogni avvio (e ogni 24h) l'EA chiama /api/ea/license/check con il numero di conto MT4: se il conto non e' registrato all'acquisto, l'EA si blocca. Un file rivenduto o condiviso e' quindi inutilizzabile su altri conti."
            : "The .ex4 file is the SAME for every client: uniqueness lives in the server-side license, not in the file. On every start (and every 24h) the EA calls /api/ea/license/check with the MT4 account number: if the account isn't registered to a purchase, the EA locks itself. A resold or shared file is therefore useless on other accounts."}
        </p>
        <ol className="space-y-2">
          {steps.map(([h, p], i) => (
            <li key={i} className="text-xs leading-relaxed">
              <span className="font-semibold text-emerald-400">{h}</span>
              <span className="text-slate-400"> — {p}</span>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "File compilati (.ex4)" : "Compiled files (.ex4)"}</h3>
        <p className="text-xs text-slate-500 mb-3">{it
          ? "Scarica il sorgente .mq4, compilalo in MetaEditor (F7) e carica qui il .ex4: solo il compilato viene distribuito agli acquirenti."
          : "Download the .mq4 source, compile in MetaEditor (F7) and upload the .ex4 here: only the compiled file is distributed to buyers."}</p>
        {["hydra", "atlas"].map((p) => (
          <div key={p} data-testid={`admin-ea-row-${p}`} className="flex flex-wrap items-center gap-3 border-t border-white/5 py-3 text-xs">
            <span className="font-mono text-white w-32 uppercase">{p}</span>
            <span className={data.files_available[p] ? "text-emerald-400" : "text-amber-400"}>
              {data.files_available[p] ? (it ? "✓ .ex4 online" : "✓ .ex4 live") : (it ? "⚠ .ex4 mancante" : "⚠ .ex4 missing")}
            </span>
            <button data-testid={`admin-ea-src-${p}`} onClick={() => downloadSrc(p)} className={smallBtn}>{it ? "Scarica sorgente" : "Download source"}</button>
            <input data-testid={`admin-ea-file-${p}`} type="file" accept=".ex4" onChange={(e) => setFiles({ ...files, [p]: e.target.files[0] })}
              className="text-xs text-slate-400 file:mr-2 file:border file:border-white/15 file:bg-transparent file:text-slate-300 file:px-2 file:py-1 file:text-xs" />
            <button data-testid={`admin-ea-upload-${p}`} onClick={() => upload(p)} disabled={!files[p] || busy === p} className={btnCls}>
              {it ? "Carica .ex4" : "Upload .ex4"}
            </button>
          </div>
        ))}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "Concedi EA a un utente (senza acquisto)" : "Grant EA to a user (without purchase)"}</h3>
        <p className="text-xs text-slate-500 mb-3">{it
          ? "Es. per te stesso, collaboratori o omaggi: l'utente vedra' l'EA nella sua pagina Download e potra' registrare il suo conto MT4."
          : "E.g. for yourself, collaborators or gifts: the user will see the EA in their Download page and can register their MT4 account."}</p>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="admin-ea-grant-email" type="email" placeholder={it ? "email utente registrato" : "registered user email"}
            value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
            className="bg-[#111111] border border-white/15 px-3 py-1.5 text-xs text-white placeholder:text-slate-600 w-64" />
          <select data-testid="admin-ea-grant-product" value={grantProduct} onChange={(e) => setGrantProduct(e.target.value)}
            className="bg-[#111111] border border-white/15 px-2 py-1.5 text-xs text-white">
            <option value="hydra">HYDRA</option>
            <option value="atlas">ATLAS</option>
          </select>
          <button data-testid="admin-ea-grant-btn" disabled={!grantEmail} className={btnCls} onClick={async () => {
            try {
              const { data: r } = await api.post("/admin/ea/grant", { email: grantEmail, product: grantProduct });
              toast.success((it ? "EA concesso a " : "EA granted to ") + r.email);
              setGrantEmail(""); load();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>{it ? "Concedi" : "Grant"}</button>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "Attiva licenza diretta (solo numero conto MT4)" : "Activate direct license (MT4 account number only)"}</h3>
        <p className="text-xs text-slate-500 mb-3">{it
          ? "Attiva un EA su un conto MT4 senza associarlo a un utente del sito: inserisci il numero del conto e il prodotto. L'EA su quel conto sara' operativo subito."
          : "Activate an EA on an MT4 account without linking it to a site user: enter the account number and product. The EA on that account works immediately."}</p>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid="admin-ea-direct-account" type="number" placeholder={it ? "numero conto MT4" : "MT4 account number"}
            value={directAcc} onChange={(e) => setDirectAcc(e.target.value)}
            className="bg-[#111111] border border-white/15 px-3 py-1.5 text-xs text-white placeholder:text-slate-600 w-48" />
          <select data-testid="admin-ea-direct-product" value={directProduct} onChange={(e) => setDirectProduct(e.target.value)}
            className="bg-[#111111] border border-white/15 px-2 py-1.5 text-xs text-white">
            <option value="hydra">HYDRA</option>
            <option value="atlas">ATLAS</option>
          </select>
          <button data-testid="admin-ea-direct-btn" disabled={!directAcc} className={btnCls} onClick={async () => {
            try {
              await api.post("/admin/ea/license/direct", { product: directProduct, mt4_account: parseInt(directAcc, 10) });
              toast.success((it ? "Licenza attivata sul conto " : "License activated on account ") + directAcc);
              setDirectAcc(""); load();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>{it ? "Attiva" : "Activate"}</button>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "Licenze attive" : "Active licenses"} ({data.licenses.length})</h3>
        {data.licenses.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-3 border-t border-white/5 py-2 text-xs font-mono">
            <span className="text-emerald-400">MT4 {l.mt4_account}</span>
            <span className="text-cyan-400 uppercase">{l.product}</span>
            <span className="text-slate-400">{l.user_email}</span>
            <span className="text-slate-600">{l.activated_at?.slice(0, 10)}</span>
            <button data-testid={`admin-ea-lic-del-${l.id}`} className={redBtn} onClick={async () => {
              await api.post("/admin/ea/license/delete", { license_id: l.id });
              toast.success(it ? "Licenza revocata" : "License revoked"); load();
            }}>{it ? "Revoca" : "Revoke"}</button>
          </div>
        ))}
        {!data.licenses.length && <p className="text-xs text-slate-600">{it ? "Nessuna licenza attiva." : "No active licenses."}</p>}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-white mb-2">{it ? "Acquisti EA" : "EA purchases"} ({data.entitlements.length})</h3>
        {data.entitlements.map((e) => {
          const lic = data.licenses.find((l) => l.user_id === e.user_id && l.product === e.product);
          return (
            <div key={e.id} className="flex flex-wrap items-center gap-3 border-t border-white/5 py-2 text-xs font-mono">
              <span className="text-slate-300">{e.user_email}</span>
              <span className="text-cyan-400 uppercase">{e.product}</span>
              <span className="text-slate-500">{e.source}</span>
              <span className="text-slate-500">{e.granted_at?.slice(0, 10)}</span>
              {lic ? (
                <>
                  <span className="text-emerald-400">MT4: {lic.mt4_account}</span>
                  <button data-testid={`admin-ea-lic-reset-${e.user_id}-${e.product}`} onClick={() => resetLic(e.user_id, e.product)} className={redBtn}>
                    {it ? "Reset licenza" : "Reset license"}
                  </button>
                </>
              ) : <span className="text-slate-600">{it ? "nessun conto registrato" : "no account registered"}</span>}
            </div>
          );
        })}
        {!data.entitlements.length && <p className="text-xs text-slate-600">{it ? "Nessun acquisto ancora." : "No purchases yet."}</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const [tab, setTab] = useState("users");
  const tabs = [
    ["users", t("admin_users"), UsersTab], ["signals", t("admin_signals"), SignalsTab],
    ["reports", t("admin_reports"), ReportsTab], ["eod", t("admin_eod"), EodTab],
    ["import", t("admin_import"), ImportTab],
    ["crypto", t("admin_crypto"), CryptoTab], ["ea", "EA", EaTab], ["stripe", "Stripe", StripeTab], ["audit", t("admin_audit"), AuditTab],
  ];
  const Active = tabs.find(([k]) => k === tab)[2];
  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white mb-6">Admin</h1>
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(([k, label]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${tab === k ? "border-emerald-500/50 text-emerald-400" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="border border-white/10 bg-[#111111] py-2"><Active /></div>
    </div>
  );
}
